import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Pencil,
  Trash2,
  KeyRound,
  Users,
  ShieldCheck,
  UserX,
  UserCheck,
  Copy,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'investor', label: 'Investor' },
  { value: 'client', label: 'Client' },
  { value: 'analyst', label: 'Analyst' },
  { value: 'operations', label: 'Operations' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'investor_relations', label: 'Investor Relations' },
];

const roleBadge = (role) => {
  const map = {
    admin: 'bg-red-100 text-red-700 border-red-200',
    investor: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    client: 'bg-blue-100 text-blue-700 border-blue-200',
    analyst: 'bg-purple-100 text-purple-700 border-purple-200',
    operations: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    compliance: 'bg-amber-100 text-amber-700 border-amber-200',
    investor_relations: 'bg-teal-100 text-teal-700 border-teal-200',
  };
  return map[role] || 'bg-gray-100 text-gray-700 border-gray-200';
};

export default function AdminUserManagementPage() {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const [page, setPage] = useState(0);
  const limit = 25;

  // Dialogs
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [resetPwOpen, setResetPwOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [tempPassword, setTempPassword] = useState('');

  // Form state
  const [form, setForm] = useState({
    email: '', first_name: '', last_name: '', password: '',
    role: 'investor', phone: '', reference_id: '',
    risk_participant_code: '', client_id: '', require_mfa: false,
    additional_emails: '',
  });

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(limit), skip: String(page * limit) });
      if (search) params.set('search', search);
      if (roleFilter !== 'all') params.set('role', roleFilter);
      if (activeFilter !== 'all') params.set('is_active', activeFilter === 'active' ? 'true' : 'false');

      const res = await fetch(`${API_URL}/api/admin/users?${params}`, { headers });
      if (!res.ok) throw new Error('Failed to fetch users');
      const data = await res.json();
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, search, roleFilter, activeFilter, page]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Debounce search
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // "Additional emails" is edited as one comma/semicolon-separated string
  // (matches the CSV bulk-import convention) but the API wants a list.
  const parseAdditionalEmails = (str) =>
    (str || '').split(/[,;]/).map(e => e.trim()).filter(Boolean);

  const handleCreate = async () => {
    try {
      const payload = { ...form, additional_emails: parseAdditionalEmails(form.additional_emails) };
      if (payload.additional_emails.length === 0) delete payload.additional_emails;
      const res = await fetch(`${API_URL}/api/admin/users`, {
        method: 'POST', headers, body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to create user');
      toast.success(data.linked_login ? 'Investor profile linked' : 'User created successfully',
        data.linked_login ? { description: data.message, duration: 10000 } : undefined);
      setCreateOpen(false);
      resetForm();
      fetchUsers();
    } catch (err) { toast.error(err.message); }
  };

  const handleEdit = async () => {
    if (!selectedUser) return;
    try {
      const payload = {};
      for (const [k, v] of Object.entries(form)) {
        if (v !== '' && v !== null && v !== undefined) payload[k] = v;
      }
      // additional_emails is always sent (even as []) so clearing the field
      // actually unlinks a previously-set profile, rather than the generic
      // "skip empty string" rule above silently leaving the old list intact.
      payload.additional_emails = parseAdditionalEmails(form.additional_emails);
      const res = await fetch(`${API_URL}/api/admin/users/${selectedUser.id}`, {
        method: 'PUT', headers, body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to update user');
      toast.success('User updated');
      setEditOpen(false);
      fetchUsers();
    } catch (err) { toast.error(err.message); }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${selectedUser.id}`, {
        method: 'DELETE', headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to delete user');
      toast.success(data.message || 'User deleted');
      setDeleteOpen(false);
      fetchUsers();
    } catch (err) { toast.error(err.message); }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${selectedUser.id}/reset-password`, {
        method: 'POST', headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to reset password');
      setTempPassword(data.temporary_password || '');
      toast.success('Password reset successfully');
    } catch (err) { toast.error(err.message); }
  };

  const openEdit = (user) => {
    setSelectedUser(user);
    setForm({
      email: user.email || '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      password: '',
      role: user.role || 'investor',
      phone: user.phone || '',
      reference_id: user.reference_id || '',
      risk_participant_code: user.risk_participant_code || '',
      client_id: user.client_id || '',
      require_mfa: !!user.require_mfa,
      additional_emails: (user.additional_emails || []).join(', '),
    });
    setEditOpen(true);
  };

  const openDelete = (user) => { setSelectedUser(user); setDeleteOpen(true); };
  const openResetPw = (user) => { setSelectedUser(user); setTempPassword(''); setResetPwOpen(true); };

  const resetForm = () => {
    setForm({ email: '', first_name: '', last_name: '', password: '', role: 'investor', phone: '', reference_id: '', risk_participant_code: '', client_id: '', require_mfa: false, additional_emails: '' });
  };

  const totalPages = Math.ceil(total / limit);

  const stats = {
    total,
    admin: users.filter(u => u.role === 'admin').length,
    investor: users.filter(u => u.role === 'investor').length,
    client: users.filter(u => u.role === 'client').length,
  };

  return (
    <div className="space-y-6" data-testid="user-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#212529] font-['Manrope']">User Management</h1>
          <p className="text-sm text-[#6C757D] mt-1">Create, edit, and manage portal accounts</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={async () => {
              try {
                const res = await fetch(`${API_URL}/api/admin/duplicate-users`, { headers });
                const data = await res.json();
                if (!res.ok) throw new Error(data.detail || 'Failed');
                const dupes = data.duplicates || [];
                if (dupes.length === 0) {
                  toast.success('No duplicate emails found');
                } else {
                  const msg = dupes.map(d => `${d._id} × ${d.count} (ids: ${d.user_ids.join(', ')})`).join('\n');
                  toast.warning(`Found ${dupes.length} duplicate email(s)`, { description: msg, duration: 30000 });
                  // eslint-disable-next-line no-console
                  console.warn('Duplicate users:', dupes);
                }
              } catch (err) { toast.error(err.message); }
            }}
            variant="outline"
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="find-duplicates-btn"
            title="Find users with duplicate emails (blocks login on select-account)"
          >
            Find Duplicates
          </Button>
          <Button
            onClick={() => { resetForm(); setCreateOpen(true); }}
            className="bg-[#1B4332] text-white hover:bg-[#143628]"
            data-testid="create-user-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> New User
          </Button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={<Users className="h-5 w-5 text-[#1B4332]" />} label="Total Users" value={total} />
        <StatCard icon={<ShieldCheck className="h-5 w-5 text-red-600" />} label="Admins" value={stats.admin} />
        <StatCard icon={<UserCheck className="h-5 w-5 text-emerald-600" />} label="Investors" value={stats.investor} />
        <StatCard icon={<UserX className="h-5 w-5 text-blue-600" />} label="Clients" value={stats.client} />
      </div>

      {/* Filters */}
      <Card className="border border-gray-100">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, email, or reference ID..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="pl-10"
                data-testid="user-search-input"
              />
            </div>
            <Select value={roleFilter} onValueChange={v => { setRoleFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[160px]" data-testid="role-filter">
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={activeFilter} onValueChange={v => { setActiveFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[160px]" data-testid="active-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border border-gray-100">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead>
                <tr className="border-b bg-gray-50/80">
                  <th className="text-left px-4 py-3 font-medium text-[#6C757D]">Name</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6C757D]">Email</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6C757D]">Role</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6C757D]">Ref ID</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6C757D]">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6C757D]">2FA</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6C757D]">Created</th>
                  <th className="text-right px-4 py-3 font-medium text-[#6C757D]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="text-center py-12 text-gray-400">Loading...</td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-12 text-gray-400">No users found</td></tr>
                ) : users.map(u => (
                  <tr key={u.id} className="border-b last:border-b-0 hover:bg-gray-50/60 transition-colors" data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3 font-medium text-[#212529]">{u.first_name} {u.last_name}</td>
                    <td className="px-4 py-3 text-[#6C757D]">{u.email}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={`${roleBadge(u.role)} text-xs capitalize`}>{u.role}</Badge>
                    </td>
                    <td className="px-4 py-3 text-[#6C757D] font-mono text-xs">{u.reference_id || u.risk_participant_code || '-'}</td>
                    <td className="px-4 py-3">
                      {u.is_active !== false
                        ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">Active</Badge>
                        : <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200 text-xs">Inactive</Badge>
                      }
                    </td>
                    <td className="px-4 py-3">
                      {u.totp_enabled ? (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">Enrolled</Badge>
                      ) : u.require_mfa ? (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">Required</Badge>
                      ) : (
                        <span className="text-xs text-gray-400">&mdash;</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#6C757D] text-xs">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('en-ZA') : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(u)} title="Edit" data-testid={`edit-user-${u.id}`}>
                          <Pencil className="h-4 w-4 text-gray-500" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openResetPw(u)} title="Reset Password" data-testid={`reset-pw-${u.id}`}>
                          <KeyRound className="h-4 w-4 text-amber-600" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openDelete(u)} title="Delete" data-testid={`delete-user-${u.id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-sm text-[#6C757D]">
                Showing {page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total}
              </p>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="prev-page">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="next-page">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Create New User</DialogTitle>
            <DialogDescription>Add a new portal account. The user will need to log in with the password you set.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>First Name *</Label><Input value={form.first_name} onChange={e => setForm(f => ({...f, first_name: e.target.value}))} data-testid="create-first-name" /></div>
              <div><Label>Last Name *</Label><Input value={form.last_name} onChange={e => setForm(f => ({...f, last_name: e.target.value}))} data-testid="create-last-name" /></div>
            </div>
            <div><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} data-testid="create-email" /></div>
            <div><Label>Password *</Label><Input type="password" value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} data-testid="create-password" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={v => setForm(f => ({...f, role: v}))}>
                  <SelectTrigger data-testid="create-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} data-testid="create-phone" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Reference ID</Label><Input placeholder="PTC-0001-00" value={form.reference_id} onChange={e => setForm(f => ({...f, reference_id: e.target.value}))} data-testid="create-ref-id" /></div>
              <div><Label>RP Code</Label><Input placeholder="01-00" value={form.risk_participant_code} onChange={e => setForm(f => ({...f, risk_participant_code: e.target.value}))} data-testid="create-rp-code" /></div>
            </div>
            {form.role === 'client' && (
              <div><Label>Client ID</Label><Input placeholder="ABC001" value={form.client_id} onChange={e => setForm(f => ({...f, client_id: e.target.value}))} data-testid="create-client-id" /></div>
            )}
            <div>
              <Label>Also accessible via (existing account's email)</Label>
              <Input
                placeholder="e.g. the person's existing investor login email"
                value={form.additional_emails}
                onChange={e => setForm(f => ({...f, additional_emails: e.target.value}))}
                data-testid="create-additional-emails"
              />
              <p className="text-xs text-[#6C757D] mt-1">
                For one person with more than one profile (e.g. investor + admin). Enter the OTHER
                account's own login email here — don't reuse it as this account's Email field above.
                At login, entering that shared email lets them pick which profile to open.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-[#1B4332] text-white hover:bg-[#143628]" data-testid="confirm-create-user">Create User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Edit User</DialogTitle>
            <DialogDescription>Update account details for {selectedUser?.email}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>First Name</Label><Input value={form.first_name} onChange={e => setForm(f => ({...f, first_name: e.target.value}))} data-testid="edit-first-name" /></div>
              <div><Label>Last Name</Label><Input value={form.last_name} onChange={e => setForm(f => ({...f, last_name: e.target.value}))} data-testid="edit-last-name" /></div>
            </div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} data-testid="edit-email" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={v => setForm(f => ({...f, role: v}))}>
                  <SelectTrigger data-testid="edit-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} data-testid="edit-phone" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Reference ID</Label><Input placeholder="PTC-0001-00" value={form.reference_id} onChange={e => setForm(f => ({...f, reference_id: e.target.value}))} data-testid="edit-ref-id" /></div>
              <div><Label>RP Code</Label><Input placeholder="01-00" value={form.risk_participant_code} onChange={e => setForm(f => ({...f, risk_participant_code: e.target.value}))} data-testid="edit-rp-code" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Active Status</Label>
                <Select value={form.is_active === false ? 'false' : 'true'} onValueChange={v => setForm(f => ({...f, is_active: v === 'true'}))}>
                  <SelectTrigger data-testid="edit-active"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Require Two-Factor Auth</Label>
                <Select value={form.require_mfa ? 'true' : 'false'} onValueChange={v => setForm(f => ({...f, require_mfa: v === 'true'}))}>
                  <SelectTrigger data-testid="edit-require-mfa"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="false">Not required</SelectItem>
                    <SelectItem value="true">Required</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.require_mfa && !selectedUser?.totp_enabled && (
              <p className="text-xs text-amber-600">
                This user hasn't enrolled yet — they'll be required to set up an authenticator app the next time they log in, before they can access anything else.
              </p>
            )}
            <div>
              <Label>Also accessible via (existing account's email)</Label>
              <Input
                placeholder="e.g. the person's existing investor login email"
                value={form.additional_emails}
                onChange={e => setForm(f => ({...f, additional_emails: e.target.value}))}
                data-testid="edit-additional-emails"
              />
              <p className="text-xs text-[#6C757D] mt-1">
                For one person with more than one profile (e.g. investor + admin). Enter the OTHER
                account's own login email here. Clearing this field removes that link.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={handleEdit} className="bg-[#1B4332] text-white hover:bg-[#143628]" data-testid="confirm-edit-user">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selectedUser?.first_name} {selectedUser?.last_name}</strong> ({selectedUser?.email})?
              This action permanently removes the account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-user">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPwOpen} onOpenChange={setResetPwOpen}>
        <DialogContent className="max-w-md" data-testid="reset-pw-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Reset Password</DialogTitle>
            <DialogDescription>
              Generate a new temporary password for {selectedUser?.email}. The user will need to change it on next login.
            </DialogDescription>
          </DialogHeader>
          {tempPassword ? (
            <div className="space-y-3 py-2">
              <Label>Temporary Password</Label>
              <div className="flex items-center gap-2">
                <Input readOnly value={tempPassword} className="font-mono" data-testid="temp-password-value" />
                <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(tempPassword); toast.success('Copied!'); }} data-testid="copy-temp-pw">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-amber-600">Share this password securely. It will not be shown again.</p>
            </div>
          ) : (
            <p className="py-4 text-sm text-[#6C757D]">Click the button below to generate a new temporary password.</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPwOpen(false)}>Close</Button>
            {!tempPassword && (
              <Button onClick={handleResetPassword} className="bg-amber-600 text-white hover:bg-amber-700" data-testid="confirm-reset-pw">Reset Password</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <Card className="border border-gray-100">
      <CardContent className="p-4 flex items-center gap-3">
        <div className="p-2 bg-gray-50 rounded-lg">{icon}</div>
        <div>
          <p className="text-xs text-[#6C757D] uppercase tracking-wide">{label}</p>
          <p className="text-xl font-bold text-[#212529]">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}
